import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type LinkEntitySuggestionResponse } from '@bliks-io/contracts';
import { apiClient } from '../client';
import { entitySuggestionKeys, recordingKeys, taskKeys } from '../query-keys';

interface LinkEntitySuggestionArgs {
  suggestionId: string;
  recordingId: string;
  /**
   * Draft-editor origin: `recordingId` is then a draft node's `stableStepId`
   * and the process comes from the route, not the body.
   */
  draftProcessId?: string;
}

/**
 * Adopt an existing pending suggestion into a recording — the manual
 * counterpart to the AI dedup. The caller writes the suggestion ref into the
 * slot client-side; this maintains the server-side `entitySuggestions[]`
 * denormalization. Invalidations mirror `useCreateEntitySuggestion` so the
 * picker placeholder and approver review list pick the link up immediately.
 */
export function useLinkEntitySuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      suggestionId,
      recordingId,
      draftProcessId
    }: LinkEntitySuggestionArgs): Promise<LinkEntitySuggestionResponse> => {
      const { data: response } = await apiClient.post<LinkEntitySuggestionResponse>(
        draftProcessId
          ? `/api/v2/entity-suggestion/${suggestionId}/link/draft/${draftProcessId}`
          : `/api/v2/entity-suggestion/${suggestionId}/link-recording`,
        { recordingId }
      );
      return response;
    },
    onSuccess: (_resp, { recordingId }) => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: entitySuggestionKeys.all });
      qc.invalidateQueries({
        queryKey: recordingKeys.stepRecording(recordingId)
      });
    }
  });
}
